import { useState } from 'react';
import { graphql } from 'gatsby';
import { applyPaddingCSS } from '@/utils/generatePadding';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { StyledSection, StyledContainer, StyledIframe } from './Iframe.styles';
import { type BlockHTMLId } from '@/utils/renderBlock';

export const trustedIframeOrigins = new Set(['https://sonarsource.github.io']);

function isTrustedIframeOrigin(iframeUrl: string): boolean {
  let origin;
  try {
    origin = new URL(iframeUrl).origin;
  } catch (error) {
    // The URL is malformed, so it is not trusted.
    // Relative URLs count as malformed, but they don't need to be trusted
    // since the browser's Same Origin Policy allows them to access the
    // embedding site anyways.
    return false;
  }
  return trustedIframeOrigins.has(origin);
}

const Iframe = ({ iframeElements, system, id }: Queries.IFRAMEFragment & BlockHTMLId) => {
  const [isLoading, setLoading] = useState(true);
  const { url, title, height, options, ...elements } = iframeElements;

  const referrerPolicy = isTrustedIframeOrigin(url.value) ? 'unsafe-url' : undefined;
  const hideOnMobile = options?.value?.includes('hide_on_mobile') ?? false;
  const hasBackground = options?.value?.includes('background') ?? false;
  const isFullWidth = options?.value?.includes('full_width') ?? false;
  const forwardUrlParams = options?.value?.includes('forward_url_parameters') ?? false;

  const getUrlWithParams = (url: string): string => {
    if (typeof window === 'undefined' || !window.location.search || !forwardUrlParams) return url;

    const params = window.location.search;

    if (url.includes('?')) {
      return `${url}&${params.slice(1)}`; // Remove the leading '?' from params
    }

    return `${url}${params}`;
  };

  return (
    <StyledSection
      data-codename={system.codename}
      hasBackground={hasBackground}
      css={applyPaddingCSS(elements)}
      id={id}
      isFullWidth={isFullWidth}
    >
      <StyledContainer>
        {isLoading && <LoadingSpinner />}
        <StyledIframe
          loading="eager"
          src={getUrlWithParams(url.value)}
          title={title.value}
          referrerPolicy={referrerPolicy}
          onLoad={() => setLoading(false)}
          height={height?.value}
          hideOnMobile={hideOnMobile}
        />
      </StyledContainer>
    </StyledSection>
  );
};

export const fragment = graphql`
  fragment IFRAME on kontent_item_iframe {
    __typename
    system {
      codename
    }
    iframeElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      title {
        value
      }
      url {
        value
      }
      height {
        value
      }
      options {
        value
      }
    }
  }
`;

export default Iframe;
